


import { computed, defineComponent, onMounted, onUnmounted, ref, watch } from "@vue/composition-api";
import { PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import {
  ContentFeed,
  ContentFeedTypeEnum,
  InputFollowingContentFeedSearch,
} from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import useAuthUser from "@/hooks/useAuthUser";
import useContentFeed from "@/hooks/useContentFeed";
import useMySubscriptionLevels from "@/hooks/useMySubscriptionLevels";
import useNSFWUserSettings from "@/hooks/useNSFWUserSettings";
import usePager from "@/hooks/usePager";
import usePaymentPackages from "@/hooks/usePaymentPackages";
import { getPurchasedContentFeeds } from "@/lib/contentFeedHelper";
import emitter from "@/lib/emitter";
import { getFeedTabs } from "@/lib/feedTabs";
import { feedFields } from "@/lib/fieldsHelper";
import config from "@/config";
import { EVENTS } from "@/const";
import Announcements from "@/components/Announcements";
import BasePage from "@/components/BasePage";
import ContentFeedList from "@/components/ContentFeedList";
import EmptyContent from "@/components/EmptyContent";
import FeedNsfwNotice from "@/components/FeedNsfwNotice";
import StoriesLine from "@/components/StoriesLine";
import TabsHeader from "@/components/TabsHeader";

export default defineComponent({
  name: "PageFeed",
  components: { FeedNsfwNotice, Announcements, ContentFeedList, BasePage, EmptyContent, TabsHeader, StoriesLine },
  setup() {
    const { authUser, authUserStrict } = useAuthUser();
    const { contentFeedFollowingSearch } = useContentFeed();
    const { showNotSafeForWork } = useNSFWUserSettings();
    const { getMySubscriptionLevels } = useMySubscriptionLevels();
    const { paymentPackages, getPaymentPackages } = usePaymentPackages([PaymentPackageEnum.FILESET]);
    const loading = ref(false);

    const {
      enableScroll: scroll,
      data: contentFeedResponse,
      items: contentFeedList,
      updateItem,
      removeItem,
      isLastPage,
      getFirstPage,
      getNextPage: getNext,
    } = usePager(page => {
      const data: InputFollowingContentFeedSearch = {
        followerId: authUserStrict.value.id,
        types: [ContentFeedTypeEnum.MEDIA, ContentFeedTypeEnum.EVENT, ContentFeedTypeEnum.RICH_TEXT],
        page,
        size: 10,
        sort: "PUBLISH_TIME,DESC",
        ...(!showNotSafeForWork.value ? { safeForWork: true } : {}),
      };
      const fields = feedFields();
      return contentFeedFollowingSearch(data, undefined, fields);
    });

    const emptyFeed = computed(() => !loading.value && contentFeedList.value.length === 0);
    const showNsfwNotice = computed(() => !loading.value && isLastPage.value);
    const showAnnouncements = computed(() => config.featureAnnouncements);

    const tabs = getFeedTabs();

    const onContentFeedUpdate = (contentFeed: ContentFeed) => {
      updateItem(contentFeed, (i1, i2) => i1.id === i2.id);
    };

    const deleteHandler = (feedId: string) => {
      removeItem(i => i.id === feedId);
    };

    const updateFeed = async (payload: { userId: string; feedId?: string }) => {
      if (!contentFeedResponse.value || !contentFeedResponse.value.result.length) {
        return;
      }

      contentFeedResponse.value = {
        ...contentFeedResponse.value,
        result: await getPurchasedContentFeeds(
          { authorId: payload.userId, purchasedFeedId: payload.feedId },
          contentFeedResponse.value,
          undefined,
          feedFields()
        ),
      };
    };

    const getData = async () => {
      loading.value = true;
      try {
        await Promise.all([getFirstPage(), getPaymentPackages(), getMySubscriptionLevels()]);
      } finally {
        loading.value = false;
      }
    };

    getData();

    watch(showNotSafeForWork, async () => {
      loading.value = true;
      if (!authUser.value) return;
      getFirstPage().finally(() => {
        loading.value = false;
        window.scrollTo({ top: 0 });
      });
    });

    onMounted(() => {
      emitter.on<any>(EVENTS.CONTENT_FEED_PURCHASED, updateFeed);
      emitter.on<any>(EVENTS.CONTENT_FEED_UPDATED, updateFeed);
    });
    onUnmounted(() => {
      emitter.off<any>(EVENTS.CONTENT_FEED_PURCHASED, updateFeed);
      emitter.off<any>(EVENTS.CONTENT_FEED_UPDATED, updateFeed);
    });

    return {
      loading,
      showNsfwNotice,
      tabs,
      emptyFeed,
      scroll,
      contentFeedResponse,
      contentFeedList,
      paymentPackages,
      showAnnouncements,
      getNext,
      deleteHandler,
      onContentFeedUpdate,
    };
  },
});
